import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
  makeStyles,
  shorthands,
  mergeClasses,
  Button,
  Spinner,
  Text,
  Tab,
  TabList,
  tokens,
  Card,
  CardHeader,
  Title2,
  Subtitle2,
  Badge,
  Link,
  Tooltip,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  ToolbarDivider,
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  createTableColumn,
  Body1,
  Caption1,
} from '@fluentui/react-components';
import { Copy20Regular, Dismiss20Regular, Settings20Regular, OpenRegular } from '@fluentui/react-icons';
import { apiConfig } from '../authConfig';
import { useAuth } from '../hooks/useAuth';
import TimezoneDateTime from './TimezoneDateTime';

const DEFAULT_POLARIS_PROFILING_DATA_PATH = (
  'https://hdfs-http-ipv4-mtprime-mwhe02-1.magnetar.binginternal.com:83/webhdfs/v1/'
  + 'MTPrime-MWHE02-1-Extra-0/projects-ssd/oiagent/profilingdata/PolarisSharedFolder'
);

const AIRFLOW_REQUIRED_SETTING_KEYS = new Set([
  'Namespace',
  'DagId',
  'Subcluster',
  'AirflowJobName',
]);

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    ...shorthands.padding('0'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    ...shorthands.padding(tokens.spacingHorizontalM),
    overflowY: 'auto',
    overflowX: 'hidden',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
  },
  jobGuid: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    marginLeft: '8px',
  },
  cardTitleHeader: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  copyButton: {
    visibility: 'hidden',
    padding: 0,
    minWidth: 'auto',
    minHeight: 'auto',
  },
  topRow: {
    display: 'flex',
    flexWrap: 'wrap',
    ...shorthands.gap('8px'),
    minWidth: 0,
  },
  detailsCard: {
    flex: '3 1 380px',
    minWidth: '380px',
    overflow: 'hidden',
  },
  statusCard: {
    flex: '1 1 260px',
    minWidth: '260px',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'stretch',
    ...shorthands.gap('0'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ':hover': {
      '& button': {
        visibility: 'visible',
      },
    },
  },
  detailLabelCell: {
    width: '120px',
    flexShrink: 0,
    minHeight: '26px',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.padding(tokens.spacingVerticalSNudge, tokens.spacingHorizontalSNudge),
  },
  detailValueCell: {
    flex: 1,
    minWidth: 0,
    minHeight: '26px',
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalXS),
    ...shorthands.padding(tokens.spacingVerticalSNudge, tokens.spacingHorizontalSNudge),
    overflowWrap: 'break-word',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  metricsRow: {
    display: 'flex',
    alignItems: 'stretch',
    ...shorthands.gap('0'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  metricLabelCell: {
    width: '110px',
    flexShrink: 0,
    minHeight: '26px',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.padding(tokens.spacingVerticalSNudge, tokens.spacingHorizontalSNudge),
  },
  metricValueCell: {
    flex: 1,
    minWidth: '60px',
    minHeight: '26px',
    ...shorthands.padding(tokens.spacingVerticalSNudge, tokens.spacingHorizontalSNudge),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  detailsBody: {
    display: 'none',
  },
  fieldList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(max(280px, 50%), 1fr))',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  statusField: {
    display: 'flex',
    alignItems: 'stretch',
    ...shorthands.gap('0'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  statusLabel: {
    width: '100px',
    flexShrink: 0,
    minHeight: '26px',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.padding(tokens.spacingVerticalSNudge, tokens.spacingHorizontalSNudge),
  },
  statusValue: {
    flex: 1,
    minWidth: 0,
    minHeight: '26px',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding(tokens.spacingVerticalSNudge, tokens.spacingHorizontalSNudge),
  },
  bottomRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    ...shorthands.gap('8px'),
    minWidth: 0,
  },
  halfCard: {
    flex: '1 1 320px',
    minWidth: '260px',
    overflow: 'hidden',
  },
  settingsCard: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'visible',
    maxHeight: 'none',
  },
  settingsBody: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 auto',
    minHeight: 'auto',
    ...shorthands.gap(tokens.spacingVerticalS),
    rowGap: tokens.spacingVerticalS,
    columnGap: '0',
  },
  settingsScrollArea: {
    flex: '0 0 auto',
    minHeight: 'auto',
    overflowY: 'visible',
    marginTop: '0',
    boxSizing: 'border-box',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  testRunsCard: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'hidden',
    maxHeight: 'none',
  },
  testRunsScrollArea: {
    width: '100%',
    flex: '0 0 auto',
    minHeight: 'auto',
    maxHeight: '220px',
    overflow: 'auto',
    boxSizing: 'border-box',
    minWidth: 0,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  settingsGrid: {
    display: 'flex',
    flexDirection: 'column',
  },
  settingsRow: {
    display: 'flex',
    ...shorthands.gap('0'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  settingsLabelCell: {
    minWidth: '120px',
    width: '180px',
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding(tokens.spacingVerticalSNudge, tokens.spacingHorizontalSNudge),
    wordBreak: 'break-word',
  },
  settingsValueCell: {
    flex: 1,
    minWidth: 0,
    ...shorthands.padding(tokens.spacingVerticalSNudge, tokens.spacingHorizontalSNudge),
    wordBreak: 'break-all',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...shorthands.padding('20px'),
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  cellContent: {
    display: 'block',
    minWidth: 0,
    maxWidth: '100%',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  tableScroller: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  resultsCard: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 auto',
    minHeight: 'auto',
    overflow: 'hidden',
  },
  resultsScrollArea: {
    width: '100%',
    overflow: 'auto',
    boxSizing: 'border-box',
    minWidth: 0,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  tableCell: {
    minWidth: 0,
  },
  tableHeaderCell: {
    minWidth: 0,
    overflow: 'visible',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  tableDataCell: {
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  resultsHeaderRow: {
    backgroundColor: tokens.colorPaletteDarkGreenBackground3,
  },
  resultsHeaderCell: {
    color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: '600',
    fontSize: '11px',
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  timezoneSup: {
    display: 'inline-block',
    fontSize: '0.72em',
    fontWeight: tokens.fontWeightRegular,
    color: 'currentColor',
    lineHeight: 1,
    position: 'relative',
    top: '-0.45em',
    marginLeft: tokens.spacingHorizontalXXS,
    opacity: 0.65,
  },
  actionRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    ...shorthands.padding(tokens.spacingVerticalSNudge, tokens.spacingHorizontalM),
    backgroundColor: tokens.colorNeutralBackground2,
    boxShadow: tokens.shadow2,
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  topMessageBarArea: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minWidth: 0,
    flexShrink: 0,
  },
  topMessageBar: {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  },
  detailsGridMain: {
    flex: 1,
  },
  infoBar: {
    marginTop: '4px',
  },
  statusHeader: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  statusError: {
    gridColumn: '1 / -1',
  },
  mutedText: {
    color: tokens.colorNeutralForeground4,
    marginTop: '8px',
    display: 'block',
  },
  mutedItalicText: {
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
    marginTop: '8px',
    display: 'block',
  },
  helperText: {
    color: tokens.colorNeutralForeground3,
    marginTop: '8px',
    ...shorthands.padding('0', '0', tokens.spacingVerticalS, '0'),
    display: 'block',
  },
  mediumGridText: {
    fontSize: tokens.fontSizeBase200,
  },
  compactGridText: {
    fontSize: '11px',
  },
  gridContainer: {
    width: 'max-content',
    minWidth: '100%',
    overflow: 'visible',
  },
});

const ALLOWED_EXTERNAL_HOST_SUFFIXES = ['binginternal.com', 'microsoft.com', 'eng.ms'];
const ALLOWED_EXTERNAL_HOST_EXACT = ['idwebelements'];
const ALLOWED_EXTERNAL_HOST_PREFIXES = ['cosmos', 'aad.cosmos'];

function isAllowedExternalHost(hostname) {
  if (!hostname) return false;
  const host = String(hostname).toLowerCase();
  if (ALLOWED_EXTERNAL_HOST_EXACT.includes(host)) return true;
  if (ALLOWED_EXTERNAL_HOST_PREFIXES.some((prefix) => host.startsWith(prefix))) return true;
  return ALLOWED_EXTERNAL_HOST_SUFFIXES.some((suffix) => host === suffix || host.endsWith(`.${suffix}`));
}

function sanitizeExternalUrl(value, { allowHttp = true } = {}) {
  if (typeof value !== 'string') return '';
  const raw = value.trim();
  if (!raw || /[\u0000-\u001F\u007F]/.test(raw)) return '';
  try {
    const u = new URL(raw);
    const protocol = u.protocol.toLowerCase();
    if (protocol !== 'https:' && !(allowHttp && protocol === 'http:')) {
      return '';
    }
    if (!isAllowedExternalHost(u.hostname)) {
      return '';
    }
    return u.toString();
  } catch {
    return '';
  }
}

function renderSettingValue(value) {
  const text = value == null ? '' : String(value);
  const safeHref = sanitizeExternalUrl(text);
  if (safeHref) {
    return (
      <Link href={safeHref} target="_blank" rel="noopener noreferrer">
        {text}
      </Link>
    );
  }
  return text;
}

function renderSettingKey(key) {
  const separator = ' / ';
  const separatorIndex = key.indexOf(separator);
  if (separatorIndex < 0) return key;

  return (
    <>
      <strong>{key.slice(0, separatorIndex)}</strong>
      {key.slice(separatorIndex)}
    </>
  );
}

function renderAdditionalInformationValue(value) {
  if (value === null || value === undefined) return '';

  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  if (/^https?:\/\//i.test(text.trim())) {
    const safeHref = sanitizeExternalUrl(text.trim());
    return safeHref ? (
      <Link href={safeHref} target="_blank" rel="noopener noreferrer">
        Open link
      </Link>
    ) : 'Invalid link';
  }

  return text;
}

function getStatusBadge(status) {
  if (!status) return <Badge appearance="outline" color="informative">{status || 'Unknown'}</Badge>;
  const s = status.toLowerCase();
  if (s === 'running' || s === 'ongoing')
    return <Badge appearance="tint" color="brand">{status}</Badge>;
  if (s === 'dispatch' || s === 'start')
    return <Badge appearance="filled" color="warning">{status}</Badge>;
  if (s === 'succeeded' || s === 'finished')
    return <Badge appearance="filled" color="success">{status}</Badge>;
  if (s === 'failed' || s === 'cancelled')
    return <Badge appearance="filled" color="danger">{status}</Badge>;
  if (s === 'ready')
    return <Badge appearance="filled" color="informative">{status}</Badge>;
  return <Badge appearance="outline">{status}</Badge>;
}

function toAadHost(original) {
  if (!original) return '';
  try {
    const url = new URL(original);
    if (url.hostname.toLowerCase().startsWith('cosmos')) {
      url.hostname = 'aad.' + url.hostname;
    }
    return sanitizeExternalUrl(url.toString());
  } catch {
    return sanitizeExternalUrl(original);
  }
}

function toServiceLogDateTime(value) {
  if (!value || Number.isNaN(new Date(value).getTime())) return '';
  const match = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/
  );
  return match ? `${match[1]}-${match[2]}-${match[3]} ${match[4]}:${match[5]}:${match[6]}` : '';
}

function toServiceLogDayRange(value, thresholdHours, shiftDays) {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  const base = new Date(d);
  if ((shiftDays < 0 && d.getHours() < thresholdHours) || (shiftDays > 0 && d.getHours() > thresholdHours)) {
    base.setDate(base.getDate() + shiftDays);
  }
  const pad = (n) => String(n).padStart(2, '0');
  return `${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}`;
}

function extractPairMachine(note) {
  const text = note || '';
  const match = text.match(/\[PairMachine:\s*([^\];]+)/i);
  return match?.[1]?.trim() || '';
}

function buildServiceLogLink(template, run, job) {
  if (!template) return '';
  const hydrated = template
    .replace(/\$Machine/g, run?.Machine || '')
    .replace(/\$PairMachine/g, extractPairMachine(job?.Note))
    .replace(/\$JobStart/g, toServiceLogDateTime(job?.DateStart))
    .replace(/\$JobEnd/g, toServiceLogDateTime(job?.DateEnd))
    .replace(/\$DayStart/g, toServiceLogDayRange(job?.DateStart, 2, -1))
    .replace(/\$DayEnd/g, toServiceLogDayRange(job?.DateEnd, 22, 1));
  return sanitizeExternalUrl(hydrated);
}

function buildAirflowJobLog(job, allTests) {
  const firstTest = allTests[0];
  if (!firstTest?.Settings) return '';
  const subcluster = firstTest.Settings.Subcluster || '';
  const airflowJobName = firstTest.Settings.AirflowJobName || '';
  const airflowNamespace = firstTest.Settings.Namespace || '';
  const airflowDagId = firstTest.Settings.DagId || '';
  const airflowRunId = job.Id || '';
  const subclusterToRegion = {
    'AKS-NorthCentralUS-FLEET': 'ncus',
    'AKS-WestUS2-FLEET': 'wus2',
    'AKS-EastUS2-FLEET': 'eus2',
    'AKS-KoreaSouth-FLEET': 'kors',
    'AKS-NorthEurope-FLEET': 'neu',
    'AKS-CentralUSEUAP-FLEET': 'ccy',
  };
  const shortRegion = subclusterToRegion[subcluster] || '';
  const url = 'https://titanweb.microsoft.com/superset/dashboard/22301/'
    + '?native_filters=('
    + `NATIVE_FILTER-PHPtfbmBj:(extraFormData:(filters:!((col:region,op:%27IN%27,val:!(%27${encodeURIComponent(shortRegion)}%27),overrideFilter:!f))),filterState:(value:!(%27${encodeURIComponent(shortRegion)}%27))),`
    + `NATIVE_FILTER-_d_DKqPXd:(extraFormData:(filters:!((col:job_name,op:%27IN%27,val:!(%27${encodeURIComponent(airflowJobName)}%27),overrideFilter:!f))),filterState:(value:!(%27${encodeURIComponent(airflowJobName)}%27))),`
    + `NATIVE_FILTER-G_sU6e3YG:(extraFormData:(filters:!((col:namespace,op:%27IN%27,val:!(%27${encodeURIComponent(airflowNamespace)}%27),overrideFilter:!f))),filterState:(value:!(%27${encodeURIComponent(airflowNamespace)}%27))),`
    + `NATIVE_FILTER-PiRVmnkce:(extraFormData:(filters:!((col:dag_id,op:%27IN%27,val:!(%27${encodeURIComponent(airflowDagId)}%27),overrideFilter:!f))),filterState:(value:!(%27${encodeURIComponent(airflowDagId)}%27))),`
    + `NATIVE_FILTER-XnYLQhIbc:(extraFormData:(filters:!((col:run_id,op:%27IN%27,val:!(%27${encodeURIComponent(airflowRunId)}%27),overrideFilter:!f))),filterState:(value:!(%27${encodeURIComponent(airflowRunId)}%27)))`
    + ')';
  return sanitizeExternalUrl(url);
}

function buildAirflowRunUrl(job, allTests) {
  const firstTest = allTests[0];
  if (!firstTest?.Settings) return '';
  const subcluster = firstTest.Settings.Subcluster || '';
  const airflowJobName = firstTest.Settings.AirflowJobName || '';
  const airflowNamespace = firstTest.Settings.Namespace || '';
  const airflowDagId = firstTest.Settings.DagId || '';
  const airflowRunId = job.Id || '';
  const subclusterToRegion = {
    'AKS-NorthCentralUS-FLEET': 'ncus',
    'AKS-WestUS2-FLEET': 'wus2',
    'AKS-EastUS2-FLEET': 'eus2',
    'AKS-KoreaSouth-FLEET': 'kors',
    'AKS-NorthEurope-FLEET': 'neu',
    'AKS-CentralUSEUAP-FLEET': 'ccy',
  };
  const shortRegion = subclusterToRegion[subcluster] || '';
  return sanitizeExternalUrl(`https://webproxy-${shortRegion}.magnetar.binginternal.com/airflow-ui/${airflowJobName}.${airflowNamespace}:8080/dags/${airflowDagId}/runs/${airflowRunId}/`);
}

function getLogsUrl(run, job, allTests) {
  const isAirflow = job.MachineType === 'Airflow';
  if (isAirflow) {
    return buildAirflowJobLog(job, allTests);
  }
  return toAadHost(run.TestResultsLocation) || sanitizeExternalUrl(run.TestLogsLocation || '') || '';
}

function webHdfsToPortalUrl(webHdfsPath, jobId) {
  if (!webHdfsPath || !jobId) return '';
  const parts = webHdfsPath.split('/webhdfs/v1/');
  if (parts.length < 2) return '';
  const hdfsRootPath = parts[1].replace(/^\/+|\/+$/g, '');
  const subclusterName = hdfsRootPath.split('/')[0];
  const scParts = subclusterName.split('-');
  const federation = scParts.length >= 2
    ? `${scParts[0]}-PROD-${scParts[1]}-fed`
    : subclusterName;
  return sanitizeExternalUrl(
    `https://webportal.magnetar.binginternal.com/storage/${federation}/${hdfsRootPath}/${jobId}/`
  );
}

function getPreservedDataUrl(run, job, allTests) {
  const isAirflow = job.MachineType === 'Airflow';
  if (isAirflow) {
    const firstTest = allTests[0];
    const preservedDataPath = firstTest?.Settings?.PreservedDataPath || '';
    return webHdfsToPortalUrl(preservedDataPath, job.Id);
  }
  return toAadHost(run.TestResultsLocation) || sanitizeExternalUrl(run.TestLogsLocation || '') || '';
}

function getProfilingDataUrl(job, allTests) {
  if (job.MachineType !== 'Airflow') return '';
  const settings = allTests[0]?.Settings || {};
  const perfFlag = String(settings.PerfFlag || '').trim();
  let enabled = perfFlag ? perfFlag.toLowerCase() !== 'default' : false;
  let profilingConfig = {};

  if (settings.ProfilingConfigJSON) {
    try {
      profilingConfig = typeof settings.ProfilingConfigJSON === 'string'
        ? JSON.parse(settings.ProfilingConfigJSON)
        : settings.ProfilingConfigJSON;
      if (!perfFlag) {
        enabled = String(profilingConfig?.EnableProfiling ?? '').trim().toLowerCase() === 'true';
      }
    } catch {
      enabled = false;
    }
  }

  const outputFolder = profilingConfig?.ProfilingOutputFolder
    || DEFAULT_POLARIS_PROFILING_DATA_PATH;
  return enabled ? webHdfsToPortalUrl(outputFolder, job.Id) : '';
}

function getPartnerServiceLog(run) {
  const runResults = run?.TestRunResults || [];
  for (const result of runResults) {
    const link = result?.AdditionalInformation?.PartnerServiceLog;
    if (link) return sanitizeExternalUrl(link);
  }
  return '';
}

function buildTitanPolarisLogUrl(jobId) {
  const normalizedJobId = String(jobId || '').replace(/-/g, '');
  if (!normalizedJobId) return '';
  return sanitizeExternalUrl(`https://titanweb.microsoft.com/superset/dashboard/18910/?native_filters=(NATIVE_FILTER-f9xhqfWVa:(extraFormData:(filters:!((col:jobid,op:'IN',val:!('${normalizedJobId}'),overrideFilter:!f))),filterState:(value:!('${normalizedJobId}'))))`);
}

function buildPolarisLogRootUrl(firstRun, jobId) {
  const baseUrl = toAadHost(firstRun?.TestResultsLocation || '');
  if (!baseUrl) return '';
  const jobIdText = String(jobId || '');
  const jobGuidIndex = baseUrl.indexOf(jobIdText);
  if (jobGuidIndex >= 0) {
    return baseUrl.substring(0, jobGuidIndex + jobIdText.length + 1);
  }
  return sanitizeExternalUrl(baseUrl);
}

function buildCosmosGroupJoinUrl(groupName) {
  const targetGroup = (groupName || '').trim() || 'perf_transfer_read';
  return sanitizeExternalUrl(`http://idwebelements/GroupManagement.aspx?Group=${encodeURIComponent(targetGroup)}&Operation=join`);
}

function PolarisJobDetailPage() {
  const classes = useStyles();
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  const [settingsTab, setSettingsTab] = useState('customer');
  const [resultsTab, setResultsTab] = useState('summary');
  const [resultColumnWidths, setResultColumnWidths] = useState({});
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelMessage, setCancelMessage] = useState(null);

  const jobQuery = useQuery({
    queryKey: ['polaris-job', jobId],
    queryFn: async () => {
      const token = await getAccessToken();
      const resp = await fetch(`${apiConfig.apiBaseUrl}/polaris/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`API error: ${resp.status}`);
      return resp.json();
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  const job = jobQuery.data ?? null;

  const loading = jobQuery.isLoading;
  const error = cancelError ?? jobQuery.error?.message ?? null;

  const handleCopyText = useCallback((text, fieldName) => {
    if (text) {
      navigator.clipboard.writeText(text).catch(() => {
        alert(`Failed to copy ${fieldName}`);
      });
    }
  }, []);

  const handleCopyJobId = useCallback(() => {
    handleCopyText(job?.Id, 'job ID');
  }, [job, handleCopyText]);

  const handleCancel = useCallback(async () => {
    if (!window.confirm('Are you sure you want to cancel this job?')) return;
    setCancelling(true);
    try {
      const token = await getAccessToken();
      const resp = await fetch(`${apiConfig.apiBaseUrl}/polaris/jobs/${jobId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ Tests: job?.Tests || [] }),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.message || `Failed to cancel job (status ${resp.status})`);
      }
      const data = await resp.json().catch(() => ({}));
      queryClient.setQueryData(['polaris-job', jobId], (prev) =>
        prev ? { ...prev, Status: 'Cancelled' } : prev
      );
      if (data.message && data.message !== 'Job cancelled successfully') {
        setCancelMessage(data.message);
      }
    } catch (err) {
      setCancelError(err.message || 'Failed to cancel job. Please try again.');
    } finally {
      setCancelling(false);
    }
  }, [getAccessToken, jobId, job]);
  const currentJob = job ?? {};
  const allTests = currentJob.Tests || [];
  const { allSettings, customerSettings } = useMemo(() => {
    const settingsByKey = new Map();
    const customerKeys = new Set();
    let settingConfig = {};

    try {
      settingConfig = JSON.parse(currentJob.ClientSettingConfig || '{}');
    } catch {
      // Keep payload order when the test-type setting configuration is invalid.
    }

    allTests.forEach((test, testIndex) => {
      if (test.CustomerKeys) test.CustomerKeys.forEach(key => customerKeys.add(key));
      Object.entries(test.Settings || {}).forEach(([key, value]) => {
        if (!settingsByKey.has(key)) settingsByKey.set(key, []);
        settingsByKey.get(key).push({
          testName: test.Name || `Test${testIndex + 1}`,
          value,
        });
      });
    });

    const buildDisplaySettings = (includeKey) => {
      const displaySettings = {};

      const orderedSettings = [...settingsByKey.entries()].sort(([leftKey], [rightKey]) => {
        const leftOrder = Number(settingConfig[leftKey]?.Order ?? 0);
        const rightOrder = Number(settingConfig[rightKey]?.Order ?? 0);
        return leftOrder - rightOrder;
      });

      orderedSettings.forEach(([key, entries]) => {
        if (!includeKey(key) || entries.length === 0) return;

        const firstValue = JSON.stringify(entries[0].value);
        const hasDifferentValues = entries.some(
          ({ value }) => JSON.stringify(value) !== firstValue
        );

        if (hasDifferentValues) {
          entries.forEach(({ testName, value }) => {
            displaySettings[`${testName} / ${key}`] = value;
          });
          return;
        }

        displaySettings[key] = entries[0].value;
      });

      return displaySettings;
    };

    return {
      allSettings: buildDisplaySettings(() => true),
      customerSettings: buildDisplaySettings((key) => {
        if (!customerKeys.has(key)) return false;

        const isHiddenAirflowSetting = currentJob.MachineType === 'Airflow'
          && AIRFLOW_REQUIRED_SETTING_KEYS.has(key)
          && Boolean(settingConfig[key]?.Hidden);
        return !isHiddenAirflowSetting;
      }),
    };
  }, [allTests, currentJob.ClientSettingConfig, currentJob.MachineType]);

  const displaySettings = settingsTab === 'customer' ? customerSettings : allSettings;

  const inferredRayFromRunData = useMemo(() => (
    allTests.some((test) =>
      (test.TestRuns || []).some((run) =>
        (run.TestRunResults || []).some((result) => Boolean(result?.AdditionalInformation?.PartnerServiceLog))
      )
    )
  ), [allTests]);

  const isRayTest = useMemo(
    () => /ray/i.test(currentJob.ExternalMachinePool || '') || inferredRayFromRunData,
    [currentJob.ExternalMachinePool, inferredRayFromRunData]
  );

  const rayJobLogUrl = useMemo(() => {
    if (!isRayTest) return '';
    const cluster = allTests[0]?.Settings?.Cluster || '';
    return cluster ? `https://serverlessplatform.${cluster}.binginternal.com/clusters/polaris-prod/#/jobs` : '';
  }, [isRayTest, allTests]);

  const fallbackPartnerServiceLog = useMemo(() => {
    for (const test of allTests) {
      for (const run of (test.TestRuns || [])) {
        const link = getPartnerServiceLog(run);
        if (link) return link;
      }
    }
    return '';
  }, [allTests]);

  const allTestRuns = useMemo(() => {
    const runs = [];
    allTests.forEach(test => {
      if (test.TestRuns && test.TestRuns.length > 0) {
        test.TestRuns.forEach(run => {
          runs.push({ ...run, testName: test.Name, id: `${test.Name}-${run.Machine}-${run.DateStart}` });
        });
      }
    });
    return runs;
  }, [allTests]);

  const firstTestRun = allTestRuns[0] || null;

  const helperLinks = useMemo(() => {
    const polarisLogsUrl = currentJob.EnableFrozen
      ? buildTitanPolarisLogUrl(currentJob.Id)
      : buildPolarisLogRootUrl(firstTestRun, currentJob.Id);

    const platformVcUrl = toAadHost(firstTestRun?.TestResultsLocation || '');
    const relevanceVcUrl = platformVcUrl
      ? platformVcUrl.replace('/IndexServePerformanceShare.test/', '/Relevance/')
      : '';

    const logReaderGroup = currentJob.LogReaderSecurityGroup || 'perf_transfer_read';
    const joinGroupUrl = buildCosmosGroupJoinUrl(logReaderGroup);

    return {
      polarisLogsUrl,
      platformVcUrl,
      relevanceVcUrl,
      joinGroupUrl,
      logReaderGroup,
    };
  }, [currentJob.EnableFrozen, currentJob.Id, currentJob.LogReaderSecurityGroup, firstTestRun]);

  const { allResults, resultColumnsSet, additionalInformationColumnsSet } = useMemo(() => {
    const results = [];
    const columnsSet = new Set();
    const additionalColumnsSet = new Set();

    allTests.forEach(test => {
      if (test.TestRuns) {
        test.TestRuns.forEach(run => {
          if (run.TestRunResults && run.TestRunResults.length > 0) {
            run.TestRunResults.forEach(result => {
              if (result.Counters) {
                Object.keys(result.Counters).forEach(k => columnsSet.add(k));
              }
              const additionalInformation = result.AdditionalInformation
                && typeof result.AdditionalInformation === 'object'
                && !Array.isArray(result.AdditionalInformation)
                ? result.AdditionalInformation
                : {};
              Object.keys(additionalInformation).forEach(k => additionalColumnsSet.add(k));
              results.push({
                id: `${test.Name}-${run.Machine}-${result.Name}`,
                testName: test.Name,
                machine: run.Machine,
                runName: result.Name,
                isValid: result.IsValid,
                counters: result.Counters || {},
                additionalInformation,
              });
            });
          }
        });
      }
    });

    return {
      allResults: results,
      resultColumnsSet: columnsSet,
      additionalInformationColumnsSet: additionalColumnsSet,
    };
  }, [allTests]);

  const resultConfig = useMemo(() => {
    let config = { TestRunNameLabel: 'QPS', Counters: {}, Links: {} };
    try {
      if (currentJob.ClientResultConfig) {
        const parsed = JSON.parse(currentJob.ClientResultConfig);
        config = { ...config, ...parsed };
        if (!config.Counters) config.Counters = {};
      }
    } catch {
      // ignore parse errors
    }
    return config;
  }, [currentJob.ClientResultConfig]);

  const allCounterKeys = useMemo(() => Array.from(resultColumnsSet), [resultColumnsSet]);
  const additionalInformationKeys = useMemo(
    () => Array.from(additionalInformationColumnsSet),
    [additionalInformationColumnsSet]
  );
  const configCounterKeys = useMemo(() => Object.keys(resultConfig.Counters), [resultConfig]);
  const orderedCounterKeys = useMemo(() => (
    configCounterKeys.length > 0
      ? [...allCounterKeys].sort((a, b) => {
          const oa = resultConfig.Counters[a]?.Order ?? 9999;
          const ob = resultConfig.Counters[b]?.Order ?? 9999;
          return oa - ob;
        })
      : allCounterKeys
  ), [allCounterKeys, configCounterKeys, resultConfig]);

  const summaryKeys = useMemo(
    () => orderedCounterKeys.filter(k => resultConfig.Counters[k]?.InSummary),
    [orderedCounterKeys, resultConfig]
  );
  const hasSummary = summaryKeys.length > 0;

  const formatCounter = useCallback((key, value) => {
    if (value === undefined || value === null) return '';
    const dp = resultConfig.Counters[key]?.DecimalPlace ?? 6;
    const num = parseFloat(value);
    return isNaN(num) ? value : num.toFixed(dp);
  }, [resultConfig]);

  const visibleCounterKeys = useMemo(
    () => (hasSummary && resultsTab === 'summary' ? summaryKeys : orderedCounterKeys),
    [hasSummary, resultsTab, summaryKeys, orderedCounterKeys]
  );

  const toCounterColumnId = useCallback((counterKey) => `counter:${counterKey}`, []);
  const toAdditionalInformationColumnId = useCallback(
    (key) => `additional-information:${key}`,
    []
  );

  const resultColumnIds = useMemo(() => {
    const ids = [];
    if (currentJob.MachineType !== 'Airflow') {
      ids.push('Test', 'Machine');
    }
    ids.push(
      'RunName',
      'IsValid',
      ...visibleCounterKeys.map(toCounterColumnId),
      ...additionalInformationKeys.map(toAdditionalInformationColumnId)
    );
    return ids;
  }, [
    currentJob.MachineType,
    visibleCounterKeys,
    additionalInformationKeys,
    toCounterColumnId,
    toAdditionalInformationColumnId,
  ]);

  const resultColumnRegistry = useMemo(() => {
    const registry = {};

    if (currentJob.MachineType !== 'Airflow') {
      registry.Test = createTableColumn({
        columnId: 'Test',
        renderHeaderCell: () => 'Test',
        renderCell: (item) => item.testName,
      });
      registry.Machine = createTableColumn({
        columnId: 'Machine',
        renderHeaderCell: () => 'Machine',
        renderCell: (item) => item.machine,
      });
    }

    registry.RunName = createTableColumn({
      columnId: 'RunName',
      renderHeaderCell: () => resultConfig.TestRunNameLabel,
      renderCell: (item) => item.runName,
    });

    registry.IsValid = createTableColumn({
      columnId: 'IsValid',
      renderHeaderCell: () => 'IsValid',
      renderCell: (item) => item.isValid !== undefined ? (
        <Badge appearance="filled" color={item.isValid ? 'success' : 'danger'} size="small">
          {item.isValid ? 'Pass' : 'Fail'}
        </Badge>
      ) : null,
    });

    orderedCounterKeys.forEach((col) => {
      const cfg = resultConfig.Counters[col] || {};
      const headerLabel = cfg.Name || col;
      const counterColumnId = toCounterColumnId(col);
      registry[counterColumnId] = createTableColumn({
        columnId: counterColumnId,
        renderHeaderCell: () => cfg.Description
          ? <Tooltip content={cfg.Description} relationship="description"><span>{headerLabel}</span></Tooltip>
          : headerLabel,
        renderCell: (item) => formatCounter(col, item.counters[col]),
      });
    });

    additionalInformationKeys.forEach((key) => {
      const columnId = toAdditionalInformationColumnId(key);
      registry[columnId] = createTableColumn({
        columnId,
        renderHeaderCell: () => key,
        renderCell: (item) => renderAdditionalInformationValue(item.additionalInformation[key]),
      });
    });

    return registry;
  }, [
    currentJob.MachineType,
    orderedCounterKeys,
    additionalInformationKeys,
    resultConfig,
    formatCounter,
    toCounterColumnId,
    toAdditionalInformationColumnId,
  ]);

  const resultGridColumns = useMemo(
    () => resultColumnIds.map((id) => resultColumnRegistry[id]).filter(Boolean),
    [resultColumnIds, resultColumnRegistry]
  );

  const resultBaseSizingOptions = useMemo(() => ({
    ...(currentJob.MachineType !== 'Airflow' && { Test: { minWidth: 120, defaultWidth: 120 } }),
    ...(currentJob.MachineType !== 'Airflow' && { Machine: { minWidth: 100, defaultWidth: 100 } }),
    RunName: { minWidth: 120, defaultWidth: 120 },
    IsValid: { minWidth: 70, defaultWidth: 70 },
    ...Object.fromEntries(orderedCounterKeys.map(col => [toCounterColumnId(col), { minWidth: 100, defaultWidth: 100 }])),
    ...Object.fromEntries(additionalInformationKeys.map(key => [
      toAdditionalInformationColumnId(key),
      { minWidth: 120, defaultWidth: 160 },
    ])),
  }), [
    currentJob.MachineType,
    orderedCounterKeys,
    additionalInformationKeys,
    toCounterColumnId,
    toAdditionalInformationColumnId,
  ]);

  const resultSizingOptions = useMemo(
    () => Object.fromEntries(resultColumnIds.map((id) => {
      const base = resultBaseSizingOptions[id] || { minWidth: 100, defaultWidth: 100 };
      const baseDefaultWidth = base.defaultWidth || base.minWidth || 100;
      const scaledDefaultWidth = Math.round(baseDefaultWidth * 1.4);
      const persistedWidth = resultColumnWidths[id];
      return [id, persistedWidth ? { ...base, defaultWidth: persistedWidth } : { ...base, defaultWidth: scaledDefaultWidth }];
    })),
    [resultColumnIds, resultBaseSizingOptions, resultColumnWidths]
  );

  const handleResultColumnResize = useCallback((_, data) => {
    if (!data?.columnId || typeof data.width !== 'number') return;
    setResultColumnWidths((prev) => {
      const current = prev[data.columnId];
      if (current === data.width) return prev;
      return { ...prev, [data.columnId]: data.width };
    });
  }, []);

  const resultGridSpanStyle = useMemo(() => {
    const minSpan = resultColumnIds.reduce((sum, id) => {
      const option = resultSizingOptions[id] || {};
      return sum + (option.defaultWidth || option.minWidth || 100);
    }, 0);

    const maxSpan = resultColumnIds.reduce((sum, id) => {
      const option = resultSizingOptions[id] || {};
      const base = option.defaultWidth || option.minWidth || 100;
      return sum + Math.max(base * 3, base + 240);
    }, 0);

    return {
      minWidth: `${Math.max(minSpan, 600)}px`,
      maxWidth: `${Math.max(maxSpan, minSpan)}px`,
    };
  }, [resultColumnIds, resultSizingOptions]);

  const hasResults = allResults.length > 0;
  const hasTestRuns = allTestRuns.length > 0;

  const testTypeOwners = '{' + (currentJob.Owner || '') + '}' || '';

  const testRunColumns = useMemo(() => [
    createTableColumn({ columnId: 'Status', renderHeaderCell: () => 'Status', renderCell: (item) => getStatusBadge(item.Status) }),
    createTableColumn({ columnId: 'Log', renderHeaderCell: () => 'Log', renderCell: (item) => {
      const serviceLogTemplate = currentJob.ServiceLogLink || '';
      const partnerServiceLog = getPartnerServiceLog(item) || fallbackPartnerServiceLog;

      const defaultPreservedUrl = isRayTest
        ? rayJobLogUrl
        : getPreservedDataUrl(item, currentJob, allTests);
      const profilingDataUrl = getProfilingDataUrl(currentJob, allTests);

      const logsUrl = serviceLogTemplate
        ? buildServiceLogLink(serviceLogTemplate, item, currentJob)
        : (isRayTest ? partnerServiceLog : getLogsUrl(item, currentJob, allTests));

      const showPreserved = Boolean(defaultPreservedUrl) && (Boolean(item.TestResultsLocation) || currentJob.MachineType === 'Airflow');
      if (!logsUrl && !showPreserved && !profilingDataUrl) return null;

      return (
        <>
          {logsUrl && <Link href={logsUrl} target="_blank" rel="noopener noreferrer">Logs</Link>}
          {logsUrl && showPreserved && <span style={{ whiteSpace: 'pre' }}>{' / '}</span>}
          {showPreserved && <Link href={defaultPreservedUrl} target="_blank" rel="noopener noreferrer">Preserved Data</Link>}
          {(logsUrl || showPreserved) && profilingDataUrl && <span style={{ whiteSpace: 'pre' }}>{' / '}</span>}
          {profilingDataUrl && <Link href={profilingDataUrl} target="_blank" rel="noopener noreferrer">Profiling Data</Link>}
        </>
      );
    }}),
    currentJob.MachineType === 'Airflow'
      ? createTableColumn({ columnId: 'Machine', renderHeaderCell: () => 'AirflowRunURL', renderCell: () => {
          const url = buildAirflowRunUrl(currentJob, allTests);
          return url ? <Link href={url} target="_blank" rel="noopener noreferrer">Airflow Run</Link> : null;
        }})
      : createTableColumn({ columnId: 'Machine', renderHeaderCell: () => 'Machine', renderCell: (item) => <span className={classes.cellContent}>{item.Machine}</span> }),
    createTableColumn({ columnId: 'Start', renderHeaderCell: () => <>Start <sup className={classes.timezoneSup}>PT</sup></>, renderCell: (item) => <TimezoneDateTime value={item.DateStart} className={classes.cellContent} /> }),
    createTableColumn({ columnId: 'End', renderHeaderCell: () => <>End <sup className={classes.timezoneSup}>PT</sup></>, renderCell: (item) => <TimezoneDateTime value={item.DateEnd} className={classes.cellContent} /> }),
    createTableColumn({ columnId: 'Error', renderHeaderCell: () => 'Error', renderCell: (item) => <span className={classes.cellContent} title={item.Error}>{item.Error}</span> }),
  ], [allTests, classes.cellContent, currentJob, currentJob.MachineType, fallbackPartnerServiceLog, isRayTest, rayJobLogUrl]);

  const testRunSizingOptions = useMemo(() => ({
    Status: { minWidth: 80, defaultWidth: 100 },
    Log: { minWidth: 100, defaultWidth: 160 },
    Machine: { minWidth: 80, defaultWidth: 120 },
    Start: { minWidth: 120, defaultWidth: 150 },
    End: { minWidth: 120, defaultWidth: 150 },
    Error: { minWidth: 100, defaultWidth: 200 },
  }), []);

  if (loading) {
    return (
      <div className={classes.container}>
        <div className={classes.contentWrapper}>
          <div className={classes.loading}><Spinner size="large" label="Loading job details..." /></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.container}>
        <div className={classes.actionRow}>
        </div>
        <div className={classes.contentWrapper}>
          <Text className={classes.errorText}>Error: {error}</Text>
        </div>
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className={classes.container}>
      <div className={classes.actionRow}>
        {job.Type && <Button appearance="subtle" icon={<OpenRegular />} onClick={() => navigate(`/polaris?testType=${encodeURIComponent(job.Type)}`)} title={`View ${job.Type} jobs`}>View Test Type Jobs</Button>}
        {job.Type && <Button appearance="subtle" icon={<Settings20Regular />} onClick={() => navigate('/polaris/test-types/' + encodeURIComponent(job.Type))} title={`${job.Type} settings`}>Test Type Settings</Button>}
        <ToolbarDivider />
        <Button appearance="subtle" icon={<Copy20Regular />} onClick={() => navigate('/polaris/submit', { state: { cloneJob: job } })}>
          Clone Job
        </Button>
        {job.Status && !['finished', 'succeeded', 'failed', 'cancelled'].includes(job.Status.toLowerCase()) && (
          <Button appearance="subtle" icon={<Dismiss20Regular />} onClick={handleCancel} disabled={cancelling}>
            {cancelling ? 'Cancelling...' : 'Cancel Job'}
          </Button>
        )}
      </div>
      <div className={classes.contentWrapper}>
      {cancelError && (
        <MessageBar intent="error">
          <MessageBarBody>{cancelError}</MessageBarBody>
        </MessageBar>
      )}
      {cancelMessage && (
        <MessageBar intent="warning">
          <MessageBarBody>{cancelMessage}</MessageBarBody>
        </MessageBar>
      )}
      <div className={classes.topMessageBarArea}>
        <MessageBar intent="info" layout="multiline" className={classes.topMessageBar}>
          <MessageBarBody>
            <MessageBarTitle>Troubleshooting</MessageBarTitle>
            Please leverage <Link href="https://eng.ms/docs/microsoft-ai/webxt/bing-fundamentals/lego/common-tools-and-services/aitools/legoplugin/overview" target="_blank" rel="noopener noreferrer">LEGO Plugin</Link> for error debugging. If no clue found there, reach out to the test type owners {testTypeOwners || 'Unknown owners'} for any questions or issues.
          </MessageBarBody>
        </MessageBar>
      </div>
      {/* Details + Status */}
      <div className={classes.topRow}>
        <Card className={classes.detailsCard}>
          <CardHeader header={<Subtitle2>Job Details</Subtitle2>} />
          <div className={classes.fieldList}>
              <div className={classes.detailRow}>
                <div className={classes.detailLabelCell}><Caption1 weight="semibold">ID</Caption1></div>
                <div className={classes.detailValueCell}>
                  <Body1>{job.Id}</Body1>
                  {job.Id && <Button className={classes.copyButton} appearance="subtle" icon={<Copy20Regular />} onClick={handleCopyJobId} title="Copy job ID" />}
                </div>
              </div>
              <div className={classes.detailRow}>
                <div className={classes.detailLabelCell}><Caption1 weight="semibold">Name</Caption1></div>
                <div className={classes.detailValueCell}>
                  <Body1>{job.Name}</Body1>
                  {job.Name && <Button className={classes.copyButton} appearance="subtle" icon={<Copy20Regular />} onClick={() => handleCopyText(job.Name, 'Name')} title="Copy name" />}
                </div>
              </div>
              <div className={classes.detailRow}>
                <div className={classes.detailLabelCell}><Caption1 weight="semibold">Type</Caption1></div>
                <div className={classes.detailValueCell}>
                  <Body1>{job.Type}</Body1>
                  {job.Type && <Button className={classes.copyButton} appearance="subtle" icon={<Copy20Regular />} onClick={() => handleCopyText(job.Type, 'Type')} title="Copy type" />}
                </div>
              </div>
              <div className={classes.detailRow}>
                <div className={classes.detailLabelCell}><Caption1 weight="semibold">User</Caption1></div>
                <div className={classes.detailValueCell}>
                  <Body1>{job.User}</Body1>
                  {job.User && <Button className={classes.copyButton} appearance="subtle" icon={<Copy20Regular />} onClick={() => handleCopyText(job.User, 'User')} title="Copy user" />}
                </div>
              </div>
              <div className={classes.detailRow}>
                <div className={classes.detailLabelCell}><Caption1 weight="semibold">Note</Caption1></div>
                <div className={classes.detailValueCell}>
                  <Body1>{job.Note}</Body1>
                  {job.Note && <Button className={classes.copyButton} appearance="subtle" icon={<Copy20Regular />} onClick={() => handleCopyText(job.Note, 'Note')} title="Copy note" />}
                </div>
              </div>
              <div className={classes.detailRow}>
                <div className={classes.detailLabelCell}><Caption1 weight="semibold">MachineType</Caption1></div>
                <div className={classes.detailValueCell}>
                  <Body1>{job.MachineType}</Body1>
                  {job.MachineType && <Button className={classes.copyButton} appearance="subtle" icon={<Copy20Regular />} onClick={() => handleCopyText(job.MachineType, 'MachineType')} title="Copy machine type" />}
                </div>
              </div>
              {job.SpecifiedMachine && (
                <div className={classes.detailRow}>
                  <div className={classes.detailLabelCell}><Caption1 weight="semibold">SpecifiedMachine</Caption1></div>
                  <div className={classes.detailValueCell}>
                    <Body1>{job.SpecifiedMachine}</Body1>
                    <Button className={classes.copyButton} appearance="subtle" icon={<Copy20Regular />} onClick={() => handleCopyText(job.SpecifiedMachine, 'SpecifiedMachine')} title="Copy specified machine" />
                  </div>
                </div>
              )}
              {job.MachineType !== 'Airflow' && (
                <div className={classes.detailRow}>
                  <div className={classes.detailLabelCell}><Caption1 weight="semibold">Priority</Caption1></div>
                  <div className={classes.detailValueCell}>
                    <Body1>{job.Priority}</Body1>
                    {job.Priority && <Button className={classes.copyButton} appearance="subtle" icon={<Copy20Regular />} onClick={() => handleCopyText(job.Priority, 'Priority')} title="Copy priority" />}
                  </div>
                </div>
              )}
              {job.MachineType !== 'Airflow' && (
                <div className={classes.detailRow}>
                  <div className={classes.detailLabelCell}><Caption1 weight="semibold">RunTimeout</Caption1></div>
                  <div className={classes.detailValueCell}>
                    <Body1>{allTests[0]?.RunTimeout}</Body1>
                    {allTests[0]?.RunTimeout && <Button className={classes.copyButton} appearance="subtle" icon={<Copy20Regular />} onClick={() => handleCopyText(allTests[0]?.RunTimeout, 'RunTimeout')} title="Copy run timeout" />}
                  </div>
                </div>
              )}
          </div>
        </Card>

        <Card className={classes.statusCard}>
          <CardHeader header={
            <div className={classes.statusHeader}>
              <Subtitle2>Status</Subtitle2>
              {getStatusBadge(job.Status)}
            </div>
          } />
          <div className={classes.fieldList}>
            <div className={classes.statusField}>
              <Caption1 weight="semibold" className={classes.statusLabel}>Submitted <sup className={classes.timezoneSup}>PT</sup></Caption1>
              <Body1 className={classes.statusValue}><TimezoneDateTime value={job.DateStart} /></Body1>
            </div>
            <div className={classes.statusField}>
              <Caption1 weight="semibold" className={classes.statusLabel}>Finished <sup className={classes.timezoneSup}>PT</sup></Caption1>
              <Body1 className={classes.statusValue}><TimezoneDateTime value={job.DateEnd} /></Body1>
            </div>
            <div className={classes.statusField}>
              <Caption1 weight="semibold" className={classes.statusLabel}>SignoffResult</Caption1>
              <Body1 className={classes.statusValue}>{job.SignoffResult}</Body1>
            </div>
            <div className={classes.statusField}>
              <Caption1 weight="semibold" className={classes.statusLabel}>SignoffScore</Caption1>
              <Body1 className={classes.statusValue}></Body1>
            </div>
            <div className={mergeClasses(classes.statusField, classes.statusError)}>
              <Caption1 weight="semibold" className={classes.statusLabel}>Error</Caption1>
              <Body1 className={classes.statusValue} style={{ wordBreak: 'break-word' }}>{job.Error}</Body1>
            </div>
          </div>
        </Card>
      </div>

      {/* Test settings + Test runs */}
      <div className={classes.bottomRow}>
        <Card className={mergeClasses(classes.halfCard, classes.settingsCard)}>
          <CardHeader header={<Subtitle2>Test settings</Subtitle2>} />
          <div className={classes.settingsBody}>
            <TabList selectedValue={settingsTab} onTabSelect={(_, d) => setSettingsTab(d.value)} size="small">
              <Tab value="customer">Customer Setting</Tab>
              <Tab value="all">All</Tab>
            </TabList>
            <div className={classes.settingsScrollArea}>
              {Object.keys(displaySettings).length > 0 ? (
                <div className={classes.settingsGrid}>
                  {Object.entries(displaySettings).map(([k, v]) => (
                    <div key={k} className={classes.settingsRow}>
                      <div className={classes.settingsLabelCell}><Caption1 weight="semibold">{renderSettingKey(k)}</Caption1></div>
                      <div className={classes.settingsValueCell}><Body1>{renderSettingValue(v)}</Body1></div>
                    </div>
                  ))}
                </div>
              ) : (
                <Text className={classes.mutedText}>No settings available.</Text>
              )}
            </div>
          </div>
        </Card>

        <Card className={mergeClasses(classes.halfCard, classes.testRunsCard)}>
          <CardHeader header={<Subtitle2>Test runs</Subtitle2>} />
          {hasTestRuns ? (
            <>
              <div className={classes.testRunsScrollArea}>
                <div className={classes.gridContainer}>
                  <DataGrid
                  items={allTestRuns}
                  columns={testRunColumns}
                  getRowId={(item) => item.id}
                  resizableColumns
                  columnSizingOptions={testRunSizingOptions}
                  size="small"
                  className={classes.mediumGridText}
                >
                  <DataGridHeader>
                    <DataGridRow>
                      {({ renderHeaderCell }) => (
                          <DataGridHeaderCell className={mergeClasses(classes.tableCell, classes.tableHeaderCell)}>{renderHeaderCell()}</DataGridHeaderCell>
                      )}
                    </DataGridRow>
                  </DataGridHeader>
                  <DataGridBody>
                    {({ item, rowId }) => (
                      <DataGridRow key={rowId}>
                        {({ renderCell }) => (
                          <DataGridCell className={mergeClasses(classes.tableCell, classes.tableDataCell)}>{renderCell(item)}</DataGridCell>
                        )}
                      </DataGridRow>
                    )}
                  </DataGridBody>
                </DataGrid>                </div>              </div>
              {job.MachineType !== 'Airflow' && helperLinks.polarisLogsUrl && (
                <Text size={100} className={classes.helperText}>
                  View <Link href={helperLinks.polarisLogsUrl} target="_blank" rel="noopener noreferrer">Polaris logs</Link>
                </Text>
              )}
              {job.MachineType !== 'Airflow' && firstTestRun && !currentJob.TestLogRootPath && helperLinks.platformVcUrl && (
                <Text size={100} className={classes.helperText}>
                  Check ServiceLogs, minidumps and Profiler results in Cosmos{' '}
                  <Link href={helperLinks.platformVcUrl} target="_blank" rel="noopener noreferrer">Platform Test VC</Link>
                  {' '}or{' '}
                  <Link href={helperLinks.relevanceVcUrl} target="_blank" rel="noopener noreferrer">Relevance VC</Link>.
                </Text>
              )}
              {job.MachineType !== 'Airflow' && firstTestRun && (
                <Text size={100} className={classes.helperText}>
                  To access logs in Cosmos you need to{' '}
                  <Link href={helperLinks.joinGroupUrl} target="_blank" rel="noopener noreferrer">join the {helperLinks.logReaderGroup}</Link>{' '}
                  security group.
                </Text>
              )}
            </>
          ) : (
            <Text className={classes.mutedItalicText}>No test runs just yet.</Text>
          )}
        </Card>
      </div>

      {/* Results */}
      <Card className={classes.resultsCard}>
        <CardHeader header={<Subtitle2>Results</Subtitle2>} />
        {hasResults ? (
          <>
            {hasSummary && (
              <TabList selectedValue={resultsTab} onTabSelect={(_, d) => setResultsTab(d.value)} size="small">
                <Tab value="summary">Summary</Tab>
                <Tab value="all">All</Tab>
              </TabList>
            )}
            <div className={classes.resultsScrollArea}>
                <div className={classes.gridContainer} style={resultGridSpanStyle}>
                  <DataGrid
                    items={allResults}
                    columns={resultGridColumns}
                    getRowId={(item) => item.id}
                    resizableColumns
                    onColumnResize={handleResultColumnResize}
                    columnSizingOptions={resultSizingOptions}
                    size="small"
                    className={classes.mediumGridText}
                  >
                    <DataGridHeader>
                      <DataGridRow>
                        {({ renderHeaderCell }) => (
                          <DataGridHeaderCell className={mergeClasses(classes.tableCell, classes.tableHeaderCell)}>{renderHeaderCell()}</DataGridHeaderCell>
                        )}
                      </DataGridRow>
                    </DataGridHeader>
                    <DataGridBody>
                      {({ item, rowId }) => (
                        <DataGridRow key={rowId}>
                          {({ renderCell }) => (
                            <DataGridCell className={mergeClasses(classes.tableCell, classes.tableDataCell)}>{renderCell(item)}</DataGridCell>
                          )}
                        </DataGridRow>
                      )}
                    </DataGridBody>
                  </DataGrid>
                </div>
            </div>
          </>
        ) : (
          <Text className={classes.mutedItalicText}>No results just yet.</Text>
        )}
      </Card>
      </div>
    </div>
  );
}

export default PolarisJobDetailPage;
